import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Search, RefreshCw, Send, CheckCircle2, AlertCircle, Clock, User as UserIcon, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailDiagnosticPanel = () => {
  const token = localStorage.getItem("sa_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [resending, setResending] = useState(false);
  const [testing, setTesting] = useState(false);

  // Selettori società → utenti
  const [societaList, setSocietaList] = useState([]);
  const [pickedSocietaId, setPickedSocietaId] = useState("");
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/superadmin/societies-light`, { headers })
      .then((res) => { if (!cancel) setSocietaList(res.data || []); })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pickedSocietaId) {
      setUsersList([]);
      return;
    }
    let cancel = false;
    axios.get(`${API}/superadmin/societies/${pickedSocietaId}/users-light`, { headers })
      .then((res) => { if (!cancel) setUsersList(res.data || []); })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSocietaId]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const email = (emailInput || "").trim();
    if (!email) {
      toast.error("Inserisci un indirizzo email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/superadmin/email-diagnostic?email=${encodeURIComponent(email)}`,
        { headers }
      );
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella ricerca");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!data?.user?.id) return;
    setResending(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/email-diagnostic/resend-verification`,
        { user_id: data.user.id },
        { headers }
      );
      toast.success(res.data.msg || "Email rispedita");
      // Ricarica diagnostica
      await handleSearch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setResending(false);
    }
  };

  const handleTestSend = async () => {
    const target = data?.user?.email || emailInput;
    if (!target) return;
    setTesting(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/email-diagnostic/test-send`,
        { email: target },
        { headers }
      );
      if (res.data.ok) {
        toast.success(`Test email inviata a ${target}`);
        await handleSearch();
      } else {
        toast.error("Invio fallito (vedi log)");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setTesting(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-5" data-testid="email-diagnostic-panel">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="h-5 w-5 text-indigo-600" /> Diagnostica Email
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Cerca per email per vedere stato utente, verifiche, log invii Resend e ri-spedire l'email di verifica.
        </p>
      </div>

      {/* Picker società → utente */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
          🔎 Oppure scegli l'abbonamento e l'utente
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Società / Abbonamento</Label>
            <Select value={pickedSocietaId} onValueChange={setPickedSocietaId}>
              <SelectTrigger className="bg-white" data-testid="diag-soc-select">
                <SelectValue placeholder={`Seleziona società (${societaList.length})`} />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-80">
                {societaList.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Utente</Label>
            <Select
              value=""
              onValueChange={(emailVal) => {
                if (!emailVal) return;
                setEmailInput(emailVal);
                // Triggera la ricerca con un piccolo delay (state update async)
                setTimeout(() => {
                  axios.get(
                    `${API}/superadmin/email-diagnostic?email=${encodeURIComponent(emailVal)}`,
                    { headers }
                  ).then((res) => setData(res.data)).catch((err) => {
                    toast.error(err.response?.data?.detail || "Errore");
                  });
                }, 50);
              }}
              disabled={!pickedSocietaId}
            >
              <SelectTrigger className="bg-white" data-testid="diag-user-select">
                <SelectValue placeholder={pickedSocietaId ? `Seleziona utente (${usersList.length})` : "Prima seleziona la società"} />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-80">
                {usersList.map((u) => (
                  <SelectItem key={u.id} value={u.email || ""}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${u.email_verified ? "bg-emerald-500" : "bg-amber-500"}`} />
                      <span className="truncate">{u.nome || u.email}</span>
                      {u.ruolo === "admin" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">admin</span>}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {pickedSocietaId && usersList.length === 0 && (
          <p className="text-xs text-amber-700">Nessun utente trovato per questa società.</p>
        )}
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
          ✉️ Oppure cerca direttamente per email
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="email"
              placeholder="cliente@esempio.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="pl-10 h-11"
              data-testid="email-diag-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            data-testid="email-diag-search-btn"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Cerca"}
          </Button>
        </div>
      </form>

      {/* Results */}
      {data && (
        <div className="space-y-4">
          {/* User Card */}
          {data.user ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid="user-card">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <UserIcon className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-900">{data.user.nome || "Senza nome"}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{data.user.ruolo}</span>
                  </div>
                  <p className="text-sm text-gray-700">{data.user.email}</p>
                  <p className="text-xs text-gray-400 mt-1">Creato: {fmtDate(data.user.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {data.user.email_verified ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Verificata
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                      <AlertCircle className="h-3.5 w-3.5" /> Non verificata
                    </span>
                  )}
                </div>
              </div>

              {data.societa && (
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  Società: <strong className="text-gray-700">{data.societa.nome}</strong>
                  {data.societa.stato && <span className="ml-2 px-2 py-0.5 rounded bg-gray-100">{data.societa.stato}</span>}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={handleResendVerification}
                  disabled={resending || data.user.email_verified}
                  className="h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
                  data-testid="resend-verification-btn"
                >
                  {resending ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                  Rispedisci email verifica
                </Button>
                <Button
                  onClick={handleTestSend}
                  disabled={testing}
                  variant="outline"
                  className="h-9 px-4 text-sm"
                  data-testid="test-send-btn"
                >
                  {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                  Invia email di test
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
              ⚠ Nessun utente trovato con questa email. Verifica eventuali refusi.
              {data.registration && (
                <p className="mt-2 text-xs">
                  Esiste però una registrazione in corso: <strong>{data.registration.email_admin}</strong> ({fmtDate(data.registration.created_at)})
                </p>
              )}
            </div>
          )}

          {/* Verifications */}
          {data.verifications?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" /> Token verifica (ultimi {data.verifications.length})
              </h3>
              <div className="space-y-2">
                {data.verifications.map((v, i) => (
                  <div key={i} className="text-xs flex flex-wrap items-center gap-2 p-2 rounded bg-gray-50">
                    <span className="font-mono text-gray-500">{v.token_preview}</span>
                    <span className="text-gray-400">Creato {fmtDate(v.created_at)}</span>
                    <span className="text-gray-400">Scade {fmtDate(v.expires_at)}</span>
                    {v.used ? (
                      <span className="text-emerald-700 font-bold">USATO {fmtDate(v.used_at)}</span>
                    ) : (
                      <span className="text-amber-700 font-bold">NON USATO</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email logs */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-gray-500" /> Storico invii ({data.totale_invii})
            </h3>
            {data.email_logs?.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                ⚠ Nessun invio email registrato per questo indirizzo.
                Verifica che il sistema mail (Resend/SMTP) sia configurato correttamente.
              </p>
            ) : (
              <div className="space-y-2">
                {data.email_logs.map((log, i) => (
                  <div key={i} className="border border-gray-100 rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="font-medium text-gray-800 truncate">{log.subject}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        log.status === "sent" || log.status === "delivered"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {log.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-3">
                      <span>{fmtDate(log.created_at)}</span>
                      {log.provider && <span>via {log.provider}</span>}
                      {log.message_id && <span className="font-mono">{log.message_id.slice(0, 20)}...</span>}
                    </div>
                    {log.error && (
                      <p className="text-xs text-red-600 mt-1">Errore: {log.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hint */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 text-xs text-indigo-800 space-y-2">
            <p className="font-bold">💡 Cosa fare se l'email non arriva:</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Verifica che il cliente abbia controllato <strong>SPAM, Promozioni, Tab Social</strong></li>
              <li>Verifica eventuali <strong>refusi nell'email</strong> registrata (vedi sopra)</li>
              <li>Invia un'email di <strong>test</strong> per verificare la deliverability</li>
              <li>Se il test arriva → l'email originale è in spam o filtrata. Chiedi al cliente di whitelistare il mittente</li>
              <li>Se il test NON arriva → controlla dashboard <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className="underline font-bold">Resend</a> per bounce/blocchi</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDiagnosticPanel;
